<template>
  <div class="kanban-board-wrapper">
    <div class="kanban-toolbar px-4 py-2 d-flex align-center">
      <v-switch v-model="showCancelled" class="mt-0 pt-0" hide-details dense label="Mostrar canceladas" />
      <v-spacer />
      <v-btn :loading="loading" icon small @click="loadBoard"><v-icon small>mdi-refresh</v-icon></v-btn>
    </div>
    <v-divider />
    <div class="kanban-board px-4 py-4">
      <v-skeleton-loader v-if="loading && !loaded" type="image" height="400" />
      <template v-else>
        <kanban-column v-for="column in columns" :key="column.key" :column-key="column.key" :color="column.color" :label="column.label" :items="column.items" :can-drop="canDrop" class="mr-3" @action="onCardAction" @item-added="onItemAdded" />
      </template>
    </div>

    <v-dialog v-model="confirm.open" width="440" persistent no-click-animation>
      <v-card>
        <v-toolbar flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">{{confirm.title}}</v-toolbar-title>
        </v-toolbar>
        <v-divider />
        <v-card-text class="py-5">
          <span class="body-2 text--secondary">{{confirm.message}}</span>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn :disabled="confirm.loading" outlined @click="cancelConfirm">Cancelar</v-btn>
          <v-spacer />
          <v-btn :loading="confirm.loading" :color="confirm.color || 'primary'" @click="executeConfirm">{{confirm.confirmLabel}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import GenericViewMixin from '@/mixins/GenericViewMixin'
import KanbanColumn from '@/components/supporting-documents/KanbanColumn'

const COLUMNS = [
  { key: 'pending', label: 'Solicitud pendiente', sources: ['pending'] },
  { key: 'sent', label: 'Solicitud enviada', sources: ['sent'] },
  { key: 'overdue', label: 'Solicitud atrasada', sources: ['overdue'] },
  { key: 'received', label: 'Documento recibido', sources: ['fulfilled', 'active'], color: '#F5FAF7' },
  { key: 'consumed', label: 'Documento facturado', sources: ['consumed'] },
  { key: 'expired', label: 'Documento expirado', sources: ['expired'] },
  { key: 'archived', label: 'Documento archivado', sources: ['archived'] }
]
const CANCELLED_COLUMN = { key: 'cancelled', label: 'Canceladas', sources: ['cancelled'] }

export default {
  components: {
    KanbanColumn
  },
  mixins: [
    GenericViewMixin
  ],
  props: {
    query: {
      type: Object,
      default: () => ({})
    }
  },
  data: () => ({
    boardData: {},
    columns: [],
    confirm: {
      open: false,
      loading: false,
      title: '',
      message: '',
      confirmLabel: 'Confirmar',
      color: 'primary',
      run: null,
      onCancel: null
    },
    loaded: false,
    loading: false,
    showCancelled: false
  }),
  watch: {
    query: {
      deep: true,
      handler () {
        this.loadBoard()
      }
    },
    showCancelled () {
      this.rebuildColumns()
    }
  },
  created () {
    this.loadBoard()
  },
  methods: {
    itemsFor (def) {
      const items = []
      const seen = new Set()
      def.sources.forEach((sourceKey) => {
        const sourceItems = this.boardData[sourceKey] || []
        sourceItems.forEach((item) => {
          const dedupeKey = item.kind === 'document' && item.submission_request
            ? `sr-${item.submission_request}`
            : `${item.kind}-${item.id}`
          if (seen.has(dedupeKey)) return
          seen.add(dedupeKey)
          items.push(item)
        })
      })
      return items
    },
    rebuildColumns () {
      const defs = this.showCancelled ? [...COLUMNS, CANCELLED_COLUMN] : COLUMNS
      this.columns = defs.map((def) => ({
        key: def.key,
        label: def.label,
        color: def.color,
        items: this.itemsFor(def)
      }))
    },
    loadBoard () {
      this.loading = true
      this.$store.dispatch('supportingDocuments/LIST', {
        resource: 'supporting-documents/board',
        query: this.query,
        setState: false,
        loader: false
      })
      .then((response) => {
        this.boardData = response.data || {}
        this.rebuildColumns()
        this.loaded = true
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          this.$dialog.message.error(this.parseErrors(error.response.data))
        }
      })
      .finally(() => {
        this.loading = false
      })
    },
    columnForItem (item) {
      if (item.kind === 'document') {
        if (item.status === 'fulfilled' || item.status === 'active') return 'received'
        return item.status
      }
      return item.status
    },
    canDrop ({ fromKey, toKey, item }) {
      if (fromKey === toKey) return false
      if (item.kind === 'request') {
        if (fromKey === 'pending' && toKey === 'sent') return true
        if (toKey === 'cancelled' && ['pending', 'sent', 'overdue'].includes(fromKey)) return true
        return false
      }
      if (item.kind === 'document') {
        if (fromKey === 'received' && toKey === 'archived') return true
        return false
      }
      return false
    },
    onItemAdded ({ toKey, item }) {
      const fromKey = this.columnForItem(item)
      if (fromKey === toKey) return
      if (item.kind === 'request' && fromKey === 'pending' && toKey === 'sent') {
        this.sendRequest(item, false)
        return
      }
      if (item.kind === 'request' && toKey === 'cancelled' && ['pending', 'sent', 'overdue'].includes(fromKey)) {
        this.askCancel(item, true)
        return
      }
      if (item.kind === 'document' && fromKey === 'received' && toKey === 'archived') {
        this.askArchive(item, true)
        return
      }
      this.loadBoard()
    },
    onCardAction ({ action, item }) {
      if (action === 'send' || action === 'resend') {
        this.sendRequest(item, action === 'resend')
        return
      }
      if (action === 'cancel') {
        this.askCancel(item, false)
        return
      }
      if (action === 'archive') {
        this.askArchive(item, false)
        return
      }
      if (action === 'upload') {
        this.$emit('upload-request', item)
        return
      }
      if (action === 'view_pdf') {
        this.$emit('view-pdf', item)
      }
    },
    sendRequest (item, isResend) {
      this.$store.dispatch('supportingDocuments/CREATE', {
        resource: 'supporting-documents/submission-requests',
        detail: 'send',
        id: item.id,
        payload: {},
        commitInstance: false
      })
      .then(() => {
        this.$dialog.message.success(isResend ? 'Email reenviado' : 'Solicitud enviada')
        this.loadBoard()
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
        this.loadBoard()
      })
    },
    askCancel (item, fromDrag) {
      this.confirm = {
        open: true,
        loading: false,
        title: 'Cancelar solicitud',
        message: `¿Cancelar la solicitud para ${item.customer_name || 'este cliente'}? Esta acción no se puede deshacer.`,
        confirmLabel: 'Cancelar solicitud',
        color: 'red',
        run: () => this.cancelRequest(item),
        onCancel: fromDrag ? () => this.loadBoard() : null
      }
    },
    askArchive (item, fromDrag) {
      this.confirm = {
        open: true,
        loading: false,
        title: 'Archivar documento',
        message: `¿Archivar el documento ${item.serial || ''} de ${item.customer_name || 'este cliente'}? Podrás seguir consultándolo pero dejará de estar disponible para facturación.`,
        confirmLabel: 'Archivar',
        color: 'red',
        run: () => this.archiveDocument(item),
        onCancel: fromDrag ? () => this.loadBoard() : null
      }
    },
    cancelConfirm () {
      const onCancel = this.confirm.onCancel
      this.resetConfirm()
      if (typeof onCancel === 'function') onCancel()
    },
    resetConfirm () {
      this.confirm.open = false
      this.confirm.run = null
      this.confirm.onCancel = null
    },
    executeConfirm () {
      if (typeof this.confirm.run !== 'function') return
      this.confirm.loading = true
      Promise.resolve(this.confirm.run())
        .catch(() => {})
        .finally(() => {
          this.confirm.loading = false
          this.resetConfirm()
        })
    },
    cancelRequest (item) {
      return this.$store.dispatch('supportingDocuments/CREATE', {
        resource: 'supporting-documents/submission-requests',
        detail: 'cancel',
        id: item.id,
        payload: {},
        commitInstance: false
      })
      .then(() => {
        this.$dialog.message.success('Solicitud cancelada')
        this.loadBoard()
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
        this.loadBoard()
        throw error
      })
    },
    archiveDocument (item) {
      return this.$store.dispatch('supportingDocuments/CREATE', {
        resource: 'supporting-documents/documents',
        detail: 'archive',
        id: item.id,
        payload: {},
        commitInstance: false
      })
      .then(() => {
        this.$dialog.message.success('Documento archivado')
        this.loadBoard()
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
        this.loadBoard()
        throw error
      })
    }
  }
}
</script>
<style scoped>
.kanban-board {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  overflow-x: auto;
  min-height: 600px;
}
.kanban-toolbar {
  background: transparent;
}
</style>
